import { supabase } from '@/integrations/supabase/client';

export interface CoordinatorBotConnection {
  id: string;
  user_id: string;
  telegram_user_id: number;
  telegram_username: string | null;
  phone_number: string;
  normalized_phone: string;
  verification_status: string;
  verified_at: string | null;
  created_at: string;
  attempts_count: number;
  last_attempt_at: string | null;
  user_profiles?: {
    first_name: string | null;
    last_name: string | null;
    role: string;
  };
}

/**
 * Check if the coordinator bot is configured
 */
export const isCoordinatorBotConfigured = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase.functions.invoke('telegram-coordinator-bot-setup', {
      body: { action: 'bot' }
    });
    
    if (error) {
      console.error('Error checking coordinator bot:', error);
      return false;
    }
    
    return !!data?.ok;
  } catch (error) {
    console.error('Failed to check coordinator bot:', error);
    return false;
  }
};

/**
 * Setup webhook for the coordinator bot
 */
export const setupCoordinatorBotWebhook = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('telegram-coordinator-bot-setup', {
      body: { action: 'setup' }
    });
    
    if (error) {
      console.error('Error setting up coordinator bot webhook:', error);
      return { success: false, error: error.message };
    }
    
    return { success: !!data?.setup?.ok, error: data?.error };
  } catch (error) {
    console.error('Failed to setup coordinator bot webhook:', error);
    return { success: false, error: (error as Error).message };
  }
};

/**
 * Get webhook info for the coordinator bot
 */
export const getCoordinatorBotWebhookInfo = async (): Promise<any> => {
  try {
    const { data, error } = await supabase.functions.invoke('telegram-coordinator-bot-setup', {
      body: { action: 'webhook' }
    });
    
    if (error) {
      console.error('Error getting coordinator bot webhook info:', error);
      return null;
    }
    
    return data?.webhook;
  } catch (error) {
    console.error('Failed to get coordinator bot webhook info:', error);
    return null;
  }
};

/**
 * Get coordinator bot connection for a user
 */
export const getCoordinatorBotConnection = async (userId: string): Promise<CoordinatorBotConnection | null> => {
  try {
    const { data, error } = await supabase
      .from('telegram_coordinator_phone_verifications')
      .select(`
        *,
        user_profiles (
          first_name,
          last_name,
          role
        )
      `)
      .eq('user_id', userId)
      .eq('verification_status', 'verified')
      .order('verified_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (error || !data) {
      return null;
    }
    
    return data as CoordinatorBotConnection;
  } catch (error) {
    console.error('Failed to get coordinator bot connection:', error);
    return null;
  }
};

/**
 * Get all coordinator bot connections
 */
export const getAllCoordinatorBotConnections = async (): Promise<CoordinatorBotConnection[]> => {
  try {
    const { data, error } = await supabase
      .from('telegram_coordinator_phone_verifications')
      .select(`
        *,
        user_profiles (
          first_name,
          last_name,
          role
        )
      `)
      .eq('verification_status', 'verified')
      .order('verified_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching coordinator bot connections:', error);
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('Failed to get coordinator bot connections:', error);
    return [];
  }
};

/**
 * Get coordinator bot statistics
 */
export const getCoordinatorBotStats = async (): Promise<{
  totalConnections: number;
  verifiedConnections: number;
  pendingConnections: number;
}> => {
  try {
    const { data, error } = await supabase
      .from('telegram_coordinator_phone_verifications')
      .select('verification_status');
    
    if (error) {
      console.error('Error fetching coordinator bot stats:', error);
      return { totalConnections: 0, verifiedConnections: 0, pendingConnections: 0 };
    }
    
    const totalConnections = data?.length || 0;
    const verifiedConnections = data?.filter(item => item.verification_status === 'verified').length || 0;
    const pendingConnections = data?.filter(item => item.verification_status === 'pending').length || 0;
    
    return {
      totalConnections,
      verifiedConnections,
      pendingConnections
    };
  } catch (error) {
    console.error('Failed to get coordinator bot stats:', error);
    return { totalConnections: 0, verifiedConnections: 0, pendingConnections: 0 };
  }
};

/**
 * Disconnect a user from the coordinator bot
 */
export const disconnectCoordinatorUser = async (userId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('telegram_coordinator_phone_verifications')
      .delete()
      .eq('user_id', userId);
    
    if (error) {
      console.error('Error disconnecting coordinator user:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true };
  } catch (error) {
    console.error('Failed to disconnect coordinator user:', error);
    return { success: false, error: (error as Error).message };
  }
};